// Picking screenshots and recordings for a support request or reply. The checks here are a courtesy
// so people get a fast, friendly "no" — the API re-runs all of them and sniffs the bytes.
import {
  SUPPORT_ACCEPTED_SUMMARY,
  SUPPORT_MAX_FILES,
  SUPPORT_MAX_FILE_BYTES,
  SUPPORT_MAX_TOTAL_BYTES,
  formatBytes,
  isSupportExtension,
  supportExtension,
} from "@gmx/shared/support-files";

const CLIPBOARD_EXT: Record<string, string> = { "image/png": "png", "image/jpeg": "jpg", "image/gif": "gif", "image/webp": "webp" };

/** `20261009-142530` — sortable, filename-safe and readable. */
export function stamp(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

/**
 * Images on the clipboard (a Win+Shift+S / ⌘⇧4 snip, a copied image). Clipboard files usually arrive
 * named "image.png" or with no name at all, so each gets a real, accepted name from its type.
 * Returns [] for an ordinary text paste, which should be left alone.
 */
export function clipboardImages(data: DataTransfer | null, at = new Date()): File[] {
  if (!data) return [];
  const listed = Array.from(data.files ?? []);
  const files = listed.length
    ? listed
    : Array.from(data.items ?? []).flatMap((item) => {
        if (item.kind !== "file") return [];
        const file = item.getAsFile();
        return file ? [file] : [];
      });
  return files
    .filter((f) => f.type.startsWith("image/"))
    .map((file, i) => new File([file], `pasted-${stamp(at)}${i ? `-${i + 1}` : ""}.${CLIPBOARD_EXT[file.type] ?? "png"}`, { type: file.type || "image/png" }));
}

/** Files dragged in from the desktop or another tab. */
export function droppedFiles(data: DataTransfer | null): File[] {
  return Array.from(data?.files ?? []);
}

/**
 * Add a batch to the current selection with the limits the API enforces: type, per-file and total
 * size, count, and no duplicates. Pure, so capture, paste, drop and the file picker share one rule.
 */
export function mergeFiles(current: File[], incoming: File[]): { files: File[]; error: string } {
  const next = [...current];
  let error = "";
  for (const file of incoming) {
    if (next.length >= SUPPORT_MAX_FILES) {
      error = `Up to ${SUPPORT_MAX_FILES} files at a time.`;
      break;
    }
    if (!isSupportExtension(supportExtension(file.name))) {
      error = `${file.name} isn't a screenshot or recording. Attach ${SUPPORT_ACCEPTED_SUMMARY}.`;
      continue;
    }
    if (file.size > SUPPORT_MAX_FILE_BYTES) {
      error = `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(SUPPORT_MAX_FILE_BYTES)} per file.`;
      continue;
    }
    if (next.some((f) => f.name === file.name && f.size === file.size)) continue;
    if (next.reduce((n, f) => n + f.size, 0) + file.size > SUPPORT_MAX_TOTAL_BYTES) {
      error = `That's over ${formatBytes(SUPPORT_MAX_TOTAL_BYTES)} of attachments.`;
      continue;
    }
    next.push(file);
  }
  return { files: next, error };
}

/** Swap one file for its edited version (crop / black out / annotate), keeping its place. */
export function replaceFile(files: File[], index: number, edited: File): File[] {
  return files.map((f, i) => (i === index ? edited : f));
}

export function isImage(file: { type?: string; mime?: string; name: string }): boolean {
  const type = file.mime ?? file.type ?? "";
  return type ? type.startsWith("image/") : /^(png|jpe?g|gif|webp)$/.test(supportExtension(file.name));
}

/** The OS snip shortcut, so the hint names the right keys. */
export function snipShortcut(platform = typeof navigator === "undefined" ? "" : `${navigator.platform} ${navigator.userAgent}`): string {
  return /mac|iphone|ipad/i.test(platform) ? "⌘ ⇧ 4" : "Win + Shift + S";
}

/** Multipart body for the support routes: fields plus `files`. */
export function supportForm(fields: Record<string, string | null | undefined>, files: File[]): FormData {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== null) form.set(k, v);
  for (const f of files) form.append("files", f, f.name);
  return form;
}
